import { useEffect, useState } from 'react';
import { useRepositories } from '../../../infrastructure/http/RepositoryProvider';
import type { CropPreset, Lot, LotInput, LotStage } from '../../../domain/Lot';
import type { Device } from '../../../domain/Device';
import type { Site } from '../../../domain/Site';

const EMPTY: LotInput = { name: '', siteId: null, areaHa: null, crop: '', baseTemp: 0, stages: [], cycleStart: null, stationIds: [], notifyStageChange: true };

// Alta y edición de un lote (id = undefined: lote nuevo). Las estaciones que
// se pueden sumar son las propias; si una ya está en otro lote, pasa a este.
// admin: lo edita el panel de admin, con los campos y estaciones del dueño.
export function useLotForm(id: string | undefined, admin = false) {
  const { lots: lotRepo, sites: siteRepo, devices: deviceRepo, admin: adminRepo } = useRepositories();
  const [form, setForm] = useState<LotInput>(EMPTY);
  const [presets, setPresets] = useState<CropPreset[]>([]);
  const [sites, setSites] = useState<Pick<Site, 'id' | 'name'>[]>([]);
  const [stations, setStations] = useState<Pick<Device, 'id' | 'name' | 'components'>[]>([]);
  // En qué lote está cada estación (para avisar que se mueve).
  const [lotOfStation, setLotOfStation] = useState<Record<string, { id: string; name: string }>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Las estaciones las cambia el dueño (o el admin por él); un invitado con
  // permiso de edición no las toca.
  const [canEditStations, setCanEditStations] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fill = (current: Lot) => {
      setCanEditStations(admin || current.access === 'owner');
      setForm({
        name: current.name,
        siteId: current.siteId,
        areaHa: current.areaHa,
        crop: current.crop,
        baseTemp: current.baseTemp,
        stages: current.stages,
        cycleStart: current.cycleStart,
        stationIds: current.stations.map((st) => st.id),
        notifyStageChange: current.notifyStageChange,
      });
    };
    const load = async () => {
      const { presets: p } = await lotRepo.presets();
      if (cancelled) return;
      setPresets(p);
      if (admin && id) {
        const [options, { lot }] = await Promise.all([adminRepo.fetchLotFormOptions(id), adminRepo.fetchLot(id)]);
        if (cancelled) return;
        setSites(options.sites);
        setStations(options.stations);
        setLotOfStation(options.lotOfStation);
        fill(lot);
        return;
      }
      const [{ sites: s }, { devices }, { lots }] = await Promise.all([siteRepo.list(), deviceRepo.list(), lotRepo.list()]);
      if (cancelled) return;
      setSites(s);
      setStations(devices.filter((d) => d.access === 'owner'));
      const byStation: Record<string, { id: string; name: string }> = {};
      lots.filter((l) => l.access === 'owner').forEach((l) => l.stations.forEach((st) => (byStation[st.id] = { id: l.id, name: l.name })));
      setLotOfStation(byStation);
      const current = id ? lots.find((l) => l.id === id) : undefined;
      if (current) fill(current);
      else if (p[0]) setForm({ ...EMPTY, crop: p[0].crop, baseTemp: p[0].baseTemp, stages: p[0].stages });
    };
    load()
      .catch((err) => !cancelled && setError(err instanceof Error ? err.message : 'No se pudo cargar el formulario.'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [id, admin, lotRepo, siteRepo, deviceRepo, adminRepo]);

  const set = <K extends keyof LotInput>(key: K, value: LotInput[K]) => setForm((f) => ({ ...f, [key]: value }));

  // Elegir un cultivo precargado trae su temperatura base y su tabla de etapas.
  const applyPreset = (key: string) => {
    const preset = presets.find((p) => p.key === key);
    if (preset) setForm((f) => ({ ...f, crop: preset.crop, baseTemp: preset.baseTemp, stages: preset.stages }));
  };

  const setStage = (index: number, patch: Partial<LotStage>) =>
    setForm((f) => ({ ...f, stages: f.stages.map((s, i) => (i === index ? { ...s, ...patch } : s)) }));
  const addStage = () =>
    setForm((f) => ({
      ...f,
      stages: [...f.stages, { name: '', zadoks: null, gdd: (f.stages[f.stages.length - 1]?.gdd ?? 0) + 100, watch: null }],
    }));
  const removeStage = (index: number) => setForm((f) => ({ ...f, stages: f.stages.filter((_, i) => i !== index) }));

  const toggleStation = (stationId: string) =>
    setForm((f) => ({
      ...f,
      stationIds: f.stationIds.includes(stationId) ? f.stationIds.filter((s) => s !== stationId) : [...f.stationIds, stationId],
    }));

  const save = async (): Promise<Lot | null> => {
    setSaving(true);
    setError(null);
    try {
      // El admin no toca el aviso de etapa de nadie.
      const { notifyStageChange, stationIds, ...fields } = form;
      const withStations = canEditStations ? { ...fields, stationIds } : fields;
      const { lot } =
        admin && id
          ? await adminRepo.updateLot(id, withStations)
          : id
            ? await lotRepo.update(id, { ...withStations, notifyStageChange })
            : await lotRepo.create(form);
      return lot;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar el lote.');
      return null;
    } finally {
      setSaving(false);
    }
  };

  return { form, set, presets, sites, stations, lotOfStation, canEditStations, applyPreset, setStage, addStage, removeStage, toggleStation, loading, saving, error, save };
}
